import { useMemo, useState } from 'react';
import { Callout, Card, Checkbox, Input, Toolbar, formatMoney, formatQty } from '@biovet/ui';
import { RecordGrid } from './Records.js';
import { ArAgeingScreen, ApAgeingScreen } from './CashAgeing.js';
import { DateWindow, useDebounced } from './parts.js';
import { companyToday, monthOf } from '../lib/reference-data.js';
import {
  useResource,
  type ApiContext,
  type AuditEntityRef,
  type AuditTrailRow,
  type GrossMarginRow,
  type SalesByZoneRow,
} from '../lib/api.js';
import { addDecimal } from '@biovet/ui';
import type { RecordColumn, RecordRow, RecordSet } from '../lib/design-data.js';

/**
 * Sales by zone, gross margin and the audit trail — reading PostgreSQL.
 *
 * All three were transcribed sample figures in `design-data.ts`, and their
 * entries there are deleted rather than kept as a fallback: per CLAUDE.md, a
 * report that silently shows invented numbers is worse than one that says it
 * has none. An empty book now produces an empty grid, which is the truth.
 *
 * They render through `RecordGrid` like every other register, so the filter
 * chips, the print layout and the export button stay in one place. The
 * difference from the mock versions is that the chips are **real** — each one
 * refetches over a different window rather than lighting up and doing nothing.
 */

const R = 'right' as const;

/** The three windows every management report offers, newest first. */
type Window = { label: string; from?: string; to?: string };

/**
 * Chips as a date window, resolved against the open fiscal year.
 *
 * "All time" carries no dates at all rather than a wide range, because the
 * audit log is partitioned on `logged_at` and an absent bound lets PostgreSQL
 * skip partitions that a `>= '1900-01-01'` would force it to consider.
 */
function useWindows(): Window[] {
  const { data } = useResource<ApiContext>('/context');
  const fy = data?.fiscalYear;
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';

  return useMemo(
    () => [
      { label: 'This month', from: monthStart, to: today },
      ...(fy ? [{ label: `FY ${fy.code}`, from: fy.startDate, to: fy.endDate }] : []),
      { label: 'All time' },
    ],
    [fy, monthStart, today],
  );
}

/** `{ from, to }` as query params, dropping the keys that are absent. */
function windowParams(w: Window | undefined): Record<string, string | undefined> {
  return { from: w?.from, to: w?.to };
}

function describeWindow(w: Window | undefined): string {
  if (!w) return '';
  if (!w.from && !w.to) return 'the whole book';
  return `${w.from} to ${w.to}`;
}

/**
 * The same sentence for a `DateWindow`'s two boxes, where either may be empty.
 *
 * An absent bound is a real state — `All time` clears both — and it has to read
 * as one, because a report showing one month looks exactly like a book with one
 * month in it.
 */
function describeRange(range: { from: string; to: string }): string {
  if (!range.from && !range.to) return 'all time';
  return `${range.from || 'the start of the book'} to ${range.to || 'today'}`;
}

// ── Sales by zone ────────────────────────────────────────────────────────────

export function SalesByZoneScreen() {
  const windows = useWindows();
  const [chip, setChip] = useState(0);
  const active = windows[chip] ?? windows[0];
  const { data, error, loading } = useResource<{ rows: SalesByZoneRow[] }>(
    '/reports/sales-by-zone',
    windowParams(active),
  );
  const rows = data?.rows ?? [];

  const set = useMemo<RecordSet>(() => {
    const cols: RecordColumn[] = [
      { label: 'Zone / salesperson' },
      { label: 'Invoices', align: R, mono: true, width: '90px' },
      { label: 'Qty (Kg)', align: R, mono: true, width: '120px' },
      { label: 'Net sales', align: R, mono: true, width: '150px' },
      { label: 'Target', align: R, mono: true, width: '150px' },
      { label: 'Achv %', align: R, mono: true, width: '100px' },
      { label: 'Margin %', align: R, mono: true, width: '110px' },
      { label: 'Commission', align: R, mono: true, width: '140px' },
    ];

    // The zone header rows are inserted here rather than sent by the API: the
    // query returns one row per salesperson and the grouping is presentation.
    const body: RecordRow[] = [];
    let zone: string | null = null;
    for (const r of rows) {
      if (r.zoneName !== zone) {
        zone = r.zoneName;
        body.push({ c: [zone, '', '', '', '', '', '', ''], head: true });
      }
      body.push([
        r.salespersonName,
        String(r.invoices),
        formatQty(r.qtyBase, 0),
        formatMoney(r.netSales, { decimals: 0 }),
        r.targetValue === null ? '—' : formatMoney(r.targetValue, { decimals: 0 }),
        r.achievementPct === null ? '—' : `${r.achievementPct}%`,
        r.marginPct === null ? '—' : `${r.marginPct}%`,
        formatMoney(r.commission, { decimals: 0 }),
      ]);
    }

    if (rows.length > 0) {
      const netSales = rows.reduce((a, r) => addDecimal(a, r.netSales), '0');
      const margin = rows.reduce((a, r) => addDecimal(a, r.margin), '0');
      const commission = rows.reduce((a, r) => addDecimal(a, r.commission), '0');
      const qty = rows.reduce((a, r) => addDecimal(a, r.qtyBase), '0');
      const invoices = rows.reduce((a, r) => a + r.invoices, 0);
      // The total's margin % is margin over net sales for the whole book, not
      // the mean of the rows' percentages — those weight a 50 kg line the same
      // as a 40 ton one.
      const pct = Number(netSales) === 0 ? null : (100 * Number(margin)) / Number(netSales);
      body.push({
        c: [
          'Total',
          String(invoices),
          formatQty(qty, 0),
          formatMoney(netSales, { decimals: 0 }),
          '',
          '',
          pct === null ? '—' : `${pct.toFixed(2)}%`,
          formatMoney(commission, { decimals: 0 }),
        ],
        bold: true,
        emphasis: true,
      });
    }

    return {
      crumb: 'Reports / Sales',
      title: 'Sales by zone and salesperson',
      subtitle: `Posted sales invoices · ${describeWindow(active)} · net of tax, in base currency`,
      chips: windows.map((w) => w.label),
      cols,
      rows: body,
    };
  }, [rows, windows, active]);

  return (
    <>
      {error && (
        <Callout tone="danger" title="Could not load sales by zone">
          {error}
        </Callout>
      )}
      <RecordGrid
        set={set}
        loading={loading}
        chip={chip}
        onChipChange={setChip}
        emptyTitle="No posted sales invoices in this window"
      />
    </>
  );
}

// ── Gross margin ─────────────────────────────────────────────────────────────

export function GrossMarginScreen() {
  const windows = useWindows();
  const [chip, setChip] = useState(0);
  const active = windows[chip] ?? windows[0];
  const { data, error, loading } = useResource<{ rows: GrossMarginRow[] }>(
    '/reports/gross-margin',
    windowParams(active),
  );
  const rows = data?.rows ?? [];

  const set = useMemo<RecordSet>(() => {
    const cols: RecordColumn[] = [
      { label: 'SKU', width: '150px', mono: true },
      { label: 'Product' },
      { label: 'Qty (Kg)', align: R, mono: true, width: '120px' },
      { label: 'Cost / Kg', align: R, mono: true, width: '120px' },
      { label: 'Rate / Kg', align: R, mono: true, width: '120px' },
      { label: 'Margin / Kg', align: R, mono: true, width: '120px' },
      { label: 'Margin', align: R, mono: true, width: '150px' },
      { label: 'Margin %', align: R, mono: true, width: '100px' },
    ];

    const body: RecordRow[] = rows.map((r) => [
      r.sku,
      r.productName,
      formatQty(r.qtyBase, 0),
      r.costPerUnit === null ? '—' : formatMoney(r.costPerUnit, { decimals: 2 }),
      r.ratePerUnit === null ? '—' : formatMoney(r.ratePerUnit, { decimals: 2 }),
      r.marginPerUnit === null ? '—' : formatMoney(r.marginPerUnit, { decimals: 2 }),
      formatMoney(r.margin, { decimals: 0 }),
      r.marginPct === null ? '—' : `${r.marginPct}%`,
    ]);

    if (rows.length > 0) {
      const qty = rows.reduce((a, r) => addDecimal(a, r.qtyBase), '0');
      const netSales = rows.reduce((a, r) => addDecimal(a, r.netSales), '0');
      const cogs = rows.reduce((a, r) => addDecimal(a, r.cogs), '0');
      const margin = rows.reduce((a, r) => addDecimal(a, r.margin), '0');
      const per = (total: string) =>
        Number(qty) === 0 ? '—' : (Number(total) / Number(qty)).toFixed(2);
      const pct = Number(netSales) === 0 ? null : (100 * Number(margin)) / Number(netSales);
      body.push({
        c: [
          '',
          'Weighted average',
          formatQty(qty, 0),
          per(cogs),
          per(netSales),
          per(margin),
          formatMoney(margin, { decimals: 0 }),
          pct === null ? '—' : `${pct.toFixed(2)}%`,
        ],
        bold: true,
        emphasis: true,
      });
    }

    return {
      crumb: 'Reports / Management',
      title: 'Gross margin analysis by product',
      subtitle: `What each sale earned once its cost is taken off · ${describeWindow(active)}`,
      chips: windows.map((w) => w.label),
      cols,
      rows: body,
    };
  }, [rows, windows, active]);

  return (
    <>
      {error && (
        <Callout tone="danger" title="Could not load gross margin">
          {error}
        </Callout>
      )}
      <RecordGrid
        set={set}
        loading={loading}
        chip={chip}
        onChipChange={setChip}
        emptyTitle="No posted sales invoices in this window"
      />
    </>
  );
}

// ── Audit trail ──────────────────────────────────────────────────────────────

/**
 * The audited table, as a person would name the thing.
 *
 * The column used to read `accounts · 280e632c-66fe-4756-abe2-96f74df660b7` —
 * a table name from the schema and a surrogate key that appears on no screen,
 * in no document and in no conversation. Neither half answers "what was
 * changed", which is the only question this column exists for.
 *
 * The map is the tables somebody actually recognises; anything else falls
 * through `noun()`, so a table added to `audit.audited_tables` tomorrow reads
 * as `Stock adjustment line` rather than as nothing.
 */
const ENTITY_LABEL: Record<string, string> = {
  accounts: 'Account',
  parties: 'Customer / supplier',
  products: 'Product',
  users: 'User',
  roles: 'Role',
  role_permissions: 'Role permission',
  user_roles: 'User role',
  user_scopes: 'User scope',
  user_company_access: 'Company access',
  number_series: 'Number series',
  warehouses: 'Warehouse',
  zones: 'Zone',
  cities: 'City',
  salespersons: 'Salesperson',
  branches: 'Branch',
  companies: 'Company profile',
  tax_codes: 'Tax code',
  price_lists: 'Price list',
  fiscal_years: 'Financial year',
  periods: 'Period',
  commission_rules: 'Commission rule',
  bank_accounts: 'Bank account',
  batches: 'Batch',
  product_barcodes: 'Barcode',
  product_uom_conversions: 'Unit conversion',
  fx_rates: 'FX rate',
  vouchers: 'Voucher',
  voucher_lines: 'Voucher line',
  sales_invoices: 'Sales invoice',
  sales_invoice_lines: 'Sales invoice line',
  sales_returns: 'Credit note',
  purchase_invoices: 'Purchase invoice',
  purchase_invoice_lines: 'Purchase invoice line',
  purchase_returns: 'Debit note',
  import_shipments: 'Consignment',
  stock_transfers: 'Stock transfer',
  stock_adjustments: 'Stock adjustment',
  count_sessions: 'Physical count',
  deals: 'Deal',
  deal_lines: 'Deal line',
  deal_settlements: 'Settlement',
  commission_invoices: 'Commission invoice',
  commission_runs: 'Commission run',
};

/**
 * What the Fields column says when the log recorded no column list.
 *
 * An INSERT changed nothing — it created the row — and a DELETE removed it, so
 * `changed_keys` is empty on both by design. A dash there reads as missing data
 * rather than as the two actions that legitimately have no field list.
 */
const VERB_OF: Record<string, string> = {
  INSERT: 'created',
  DELETE: 'deleted',
};

/**
 * `stock_adjustment_lines` → `Stock adjustment line`, for anything unmapped.
 *
 * The `ss` guard is not pedantry: `user_company_access` is a real audited table
 * and the naive rule renders it `User company acces`, which reads as a typo in a
 * report whose whole job is to be believed.
 */
function noun(table: string): string {
  const words = table.replace(/_/g, ' ').replace(/(?<!s)s$/, '');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * What was changed, in one cell: the kind of thing, its business key, its name.
 *
 * All three parts are optional and the fallbacks matter. A **deleted** row
 * resolves to no name — the row is gone — but its key survives in the log,
 * which is precisely the case where the audit trail is the only record left. A
 * line table has neither, and reads as its kind alone, which is honest: the
 * document above it is in the Document column of the same row.
 */
function describeRef(ref: AuditEntityRef): string {
  const label = ENTITY_LABEL[ref.entityTable] ?? noun(ref.entityTable);
  const rest = [ref.entityKey, ref.entityName].filter(Boolean).join(' · ');
  return rest ? `${label} · ${rest}` : label;
}

/**
 * Two refs are the same audited row — compared on the surrogate key, never on
 * what they render as.
 *
 * A reversal touches the original voucher and the reversal voucher, and the lead
 * row renders as the bare word `Voucher` because the Document column carries its
 * number. Comparing rendered text folded the second voucher into the first, so
 * the cell said nothing about REV-26-000001 having been created.
 */
function sameRef(a: AuditEntityRef, b: AuditEntityRef): boolean {
  return a.entityTable === b.entityTable && (a.entityId ?? '') === (b.entityId ?? '');
}

/**
 * Everything one act changed, in one cell: its subject first, then the rest.
 *
 * Posting a two-line journal writes six log entries — the voucher inserted, the
 * voucher updated once its lines exist, the voucher posted, `first_posted_at`
 * stamped on each account, and the number series advanced — and reading that as
 * six rows says six things happened when one person pressed Post once. The
 * server groups them (see `auditTrail`); this states what the group holds:
 *
 *   `Voucher, Account · 1-1-1-1 · Cash in Hand — Head Office,
 *    Account · 1-1-2-1 · The Bank of Punjab Grw, Number series (6 changes)`
 *
 * **The count is not decoration.** It is the only thing on the row that says a
 * fold happened, and a summary that hides its own summarising is not an audit
 * trail. `+N more` appears where the server capped the list rather than where
 * the act was small — the seed's chart install touches four hundred and
 * sixty-eight rows in one transaction, and naming them all would be a payload
 * nobody reads.
 */
function describeEntity(r: AuditTrailRow): string {
  const lead = describeRef(r);
  const others = r.touched.filter((t) => !sameRef(t, r));
  const parts = [lead, ...others.map(describeRef)];
  // Against what is on the row, not against what the server sent. The lead is
  // one of the distinct rows `touchedCount` counts, and it is not always inside
  // the capped list — so subtracting the list length overstates the remainder
  // by one exactly when the lead fell outside the cap.
  const hidden = r.touchedCount - parts.length;
  if (hidden > 0) parts.push(`+${hidden} more`);
  const list = parts.join(', ');
  return r.changes > 1 ? `${list}  (${r.changes} changes)` : list;
}

/**
 * Reports → Audit Trail: the forensic view, over a window somebody chose.
 *
 * It had three preset chips and no way to state a date, which is the wrong way
 * round for the one screen in the application read *because* of a date — an
 * auditor arrives with "the 14th of March" rather than with "this month". The
 * chips are gone in favour of the `DateWindow` every register and both
 * statements already wear, so From/To, the month arrows and `This month` mean
 * the same thing here as everywhere else.
 *
 * **Search is a query parameter, not a filter over `rows`.** The screen holds
 * the newest 200 entries of a table that only grows, so a browser-side filter
 * would search the page rather than the log, and answer "no such entry" about
 * an entry that is simply older than the two hundredth.
 */
export function AuditTrailScreen() {
  const [range, setRange] = useState(() => monthOf(companyToday()));
  const [search, setSearch] = useState('');
  // Off by default: one act is one row. On, every log entry is its own row —
  // which is what the table actually holds, and what a forensic question wants.
  const [raw, setRaw] = useState(false);
  const settled = useDebounced(search);
  const { data, error, loading } = useResource<{ rows: AuditTrailRow[] }>('/reports/audit-trail', {
    from: range.from || undefined,
    to: range.to || undefined,
    q: settled.trim() || undefined,
    raw: raw ? '1' : undefined,
    limit: '200',
  });
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const set = useMemo<RecordSet>(() => {
    const cols: RecordColumn[] = [
      { label: 'When', width: '160px', mono: true },
      { label: 'Who', width: '150px' },
      { label: 'Action', width: '100px', badge: true },
      { label: 'What was changed', width: '360px' },
      { label: 'Document', width: '150px', mono: true },
      { label: 'Amount', align: R, mono: true, width: '120px' },
      { label: 'Fields changed', width: '200px' },
      { label: 'IP address', width: '125px', mono: true },
    ];

    const body: RecordRow[] = rows.map((r) => [
      r.loggedAt,
      r.username,
      r.action,
      describeEntity(r),
      // A document or nothing. `document_no` carries the business key of *any*
      // audited row, so this column used to print an account code under a
      // heading that says Document.
      r.documentRef ?? '—',
      r.amountBase === null ? '—' : formatMoney(r.amountBase, { decimals: 0 }),
      // The before/after images stay in the database. What a reader wants at a
      // glance is which columns moved, not sixty columns of JSON.
      r.changedKeys && r.changedKeys.length > 0
        ? r.changedKeys.join(', ')
        : (r.reason ?? VERB_OF[r.action] ?? '—'),
      r.ipAddress ?? '—',
    ]);

    return {
      crumb: 'Reports / Compliance',
      title: 'Audit trail',
      subtitle: `${
        raw ? 'Every change written to the log' : 'One row per act, newest first'
      } · ${describeRange(range)}${
        settled.trim() ? ` · matching “${settled.trim()}”` : ''
      } · newest 200`,
      chips: [],
      cols,
      rows: body,
    };
  }, [rows, range, settled]);

  return (
    <>
      {error && (
        <Callout tone="danger" title="Could not load the audit trail">
          {error}
        </Callout>
      )}
      <RecordGrid
        set={set}
        loading={loading}
        emptyTitle="Nothing logged in this window"
        actions={
          <>
            {/* The way back to the log itself. Grouping is a reading of the
                entries, not a replacement for them, and an audit trail that
                cannot show what it actually holds is not one. */}
            <Checkbox
              label="Every change"
              checked={raw}
              onChange={(e) => setRaw(e.target.checked)}
              className="mr-1 items-center"
            />
            <Input
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="User, document, account, IP…"
              aria-label="Search the audit trail"
              className="w-[240px]"
            />
          </>
        }
      >
        <Card flush className="mb-3.5" data-noprint="1">
          <Toolbar>
            <DateWindow range={range} onChange={setRange} label="All time" />
            <div className="flex-1" />
            <span className="text-[12px] text-[var(--ink-3)]">
              {rows.length} entr{rows.length === 1 ? 'y' : 'ies'} · {describeRange(range)}
            </span>
          </Toolbar>
        </Card>
      </RecordGrid>
    </>
  );
}

// ── AR / AP ageing, together ─────────────────────────────────────────────────

/**
 * Both ageings on one screen, because the nav item says "AR / AP Ageing".
 *
 * It used to render the receivables screen alone, so half of what the label
 * promised was simply missing. Each half is the same component the dashboard's
 * Receivables and Payables tiles open, reading `/ar-ageing` and `/ap-ageing` —
 * one query each, not one combined query, because a receivable and a payable
 * age against different documents and the two views were already right.
 */
export function ArApAgeingScreen() {
  return (
    <>
      <ArAgeingScreen />
      <div className="pt-3.5">
        <ApAgeingScreen />
      </div>
    </>
  );
}

// ── Sales and Purchase registers ─────────────────────────────────────────────

/** One line of one invoice, in the register's own shape. */
interface RegisterRow {
  date: string;
  invoiceNo: string;
  supplierInvoiceNo?: string | null;
  party: string;
  partyNtn: string | null;
  partyStrn: string | null;
  product: string;
  sku: string | null;
  qty: string;
  uom: string | null;
  rate: string;
  amountExTax: string;
  taxPct: string;
  taxAmount: string;
  amountIncTax: string;
  status: string;
}

/**
 * The Sales and Purchase registers, which are one report with two sources.
 *
 * Nine columns, and they are the ones a sales-tax return is filed from:
 *
 *   Date · Invoice No. · Party Name · Product · Qty · Rate ·
 *   Amount (excluding sales tax) · Sales tax · Amount (including sales tax)
 *
 * **A row is an invoice line, not an invoice**, and that is the standard rather
 * than a detail. A register is read against the goods: an invoice carrying feed
 * at 18% beside a zero-rated line is two rows with two tax figures, and rolling
 * it into one would print a blended rate that appears on no document. It is also
 * why Product, Qty and Rate can be columns at all.
 *
 * **Nothing internal is in it.** The Sales Invoice screen shows COGS, gross
 * margin and the salesperson's commission because that screen is read inside the
 * building; a register is the report that leaves it, and the query does not
 * select those columns at all — so Print and Export cannot carry them by
 * accident. Same separation `DocumentPanel` keeps with `internal`, applied to a
 * report instead of a document.
 *
 * The totals row is what the register is *for*: the middle figure reconciles to
 * Output Sales Tax on the sales side and Input Sales Tax on the purchase side,
 * over the same window.
 */
function RegisterReport({ kind }: { kind: 'sales' | 'purchase' }) {
  const windows = useWindows();
  const [chip, setChip] = useState(0);
  const active = windows[chip] ?? windows[0];
  const sales = kind === 'sales';
  const { data, error, loading } = useResource<{ rows: RegisterRow[] }>(
    sales ? '/reports/sales-register' : '/reports/purchase-register',
    windowParams(active),
  );
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const set = useMemo<RecordSet>(() => {
    // Nine columns and no more. The party's NTN and STRN come back in the
    // payload — a statutory export will want them — and are deliberately not
    // rendered: adding a tenth column pushed Party and Product into two-word
    // wraps and clipped the last figure off the right of the page, which on a
    // register is the one column nobody may lose.
    const cols: RecordColumn[] = [
      { label: 'Date', width: '96px', mono: true },
      { label: 'Invoice no.', width: '140px', mono: true },
      { label: 'Party name' },
      { label: 'Product' },
      { label: 'Qty', align: R, mono: true, width: '92px' },
      { label: 'Rate', align: R, mono: true, width: '92px' },
      { label: 'Amount excl. tax', align: R, mono: true, width: '134px' },
      { label: 'Sales tax', align: R, mono: true, width: '124px' },
      { label: 'Amount incl. tax', align: R, mono: true, width: '134px' },
    ];

    const body: RecordRow[] = rows.map((r) => [
      r.date.slice(0, 10),
      // The supplier's own invoice number is the one on the paper a tax officer
      // asks for; ours is an internal allocation. Both, when the two differ.
      !sales && r.supplierInvoiceNo && r.supplierInvoiceNo !== r.invoiceNo
        ? `${r.invoiceNo} · ${r.supplierInvoiceNo}`
        : r.invoiceNo,
      r.party,
      r.product,
      `${formatQty(r.qty, 2)}${r.uom ? ` ${r.uom}` : ''}`,
      formatMoney(r.rate, { decimals: 2 }),
      formatMoney(r.amountExTax, { decimals: 2 }),
      // The rate is named per line, because that is the fact being reported —
      // and a register with one tax column and no rate cannot be checked.
      `${formatMoney(r.taxAmount, { decimals: 2 })}${
        Number(r.taxPct) > 0 ? `  @${Number(r.taxPct)}%` : ''
      }`,
      formatMoney(r.amountIncTax, { decimals: 2 }),
    ]);

    if (rows.length > 0) {
      const ex = rows.reduce((a, r) => addDecimal(a, r.amountExTax), '0');
      const tax = rows.reduce((a, r) => addDecimal(a, r.taxAmount), '0');
      const inc = rows.reduce((a, r) => addDecimal(a, r.amountIncTax), '0');
      const invoices = new Set(rows.map((r) => r.invoiceNo)).size;
      body.push({
        c: [
          'Total',
          `${invoices} invoice${invoices === 1 ? '' : 's'}`,
          `${rows.length} line${rows.length === 1 ? '' : 's'}`,
          '',
          '',
          '',
          formatMoney(ex, { decimals: 2 }),
          formatMoney(tax, { decimals: 2 }),
          formatMoney(inc, { decimals: 2 }),
        ],
        bold: true,
        emphasis: true,
      });
    }

    return {
      crumb: `Reports / ${sales ? 'Sales' : 'Purchase'}`,
      title: `${sales ? 'Sales' : 'Purchase'} Register`,
      subtitle: `The sales tax on every ${sales ? 'sales' : 'purchase'} invoice line · ${describeWindow(active)}`,
      chips: windows.map((w) => w.label),
      cols,
      rows: body,
    };
  }, [rows, windows, active, sales]);

  return (
    <RecordGrid
      set={set}
      loading={loading}
      error={error}
      chip={chip}
      onChipChange={setChip}
      emptyTitle={`No ${sales ? 'sales' : 'purchase'} invoice in this period`}
    >
      {rows.length === 0 && !loading && !error && (
        <Callout tone="info" title="Nothing to report yet">
          The register is built from posted {sales ? 'sales invoices' : 'purchase invoices'}. None
          falls in {describeWindow(active)} — widen the period, or raise one first.
        </Callout>
      )}
    </RecordGrid>
  );
}

export function SalesRegisterScreen() {
  return <RegisterReport kind="sales" />;
}

export function PurchaseRegisterScreen() {
  return <RegisterReport kind="purchase" />;
}
